<template>
    <div v-if="!displayBodyRows.length" class="text-gray-500 text-sm py-4">
        {{ emptyMessage }}
    </div>
    <div v-else class="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
        <table class="w-full min-w-[640px] border-collapse text-left text-sm text-gray-800">
            <thead>
                <tr class="border-b border-gray-200 bg-gray-50">
                    <th
                        v-for="(cellHtml, i) in headerCells"
                        :key="'h-' + i"
                        scope="col"
                        class="px-4 py-3 font-bold text-xs uppercase tracking-wider text-gray-500 sm:px-5 sm:py-4"
                        :class="i >= 2 ? 'text-right' : ''"
                        v-html="cellHtml"
                    />
                </tr>
            </thead>
            <tbody class="divide-y divide-gray-100">
                <tr
                    v-for="(row, ri) in displayBodyRows"
                    :key="'r-' + ri + '-' + currencyDisplayKey"
                    class="hover:bg-gray-50/80 transition-colors"
                >
                    <td
                        v-for="(cell, ci) in row"
                        :key="'c-' + ri + '-' + ci + '-' + currencyDisplayKey"
                        :class="tdClass(cell, ci)"
                    >
                        <template v-if="cell.kind === 'price' && Number.isFinite(cell.baseHuf) && cell.baseHuf >= 0">
                            <div class="flex flex-col items-end gap-1.5">
                                <div
                                    v-if="cell.product && cell.feeHuf > 0"
                                    class="w-full space-y-0.5 text-right"
                                >
                                    <div class="text-[11px] leading-tight text-gray-500">
                                        {{ labelOfficial }}
                                        <span class="font-medium text-gray-700 tabular-nums">{{
                                            formatHufDisplay(cell.baseHuf)
                                        }}</span>
                                    </div>
                                    <div
                                        v-if="cell.feeHuf > 0"
                                        class="text-[11px] leading-tight text-gray-500"
                                    >
                                        {{ labelFee }}
                                        <span class="font-medium text-gray-700 tabular-nums">{{
                                            formatHufDisplay(cell.feeHuf)
                                        }}</span>
                                    </div>
                                    <div
                                        class="text-sm font-black text-hu-green tabular-nums"
                                    >
                                        {{ labelTotal }}
                                        {{ formatHufDisplay(cell.totalHuf) }}
                                    </div>
                                </div>
                                <span
                                    v-else
                                    class="inline-block whitespace-nowrap tabular-nums font-medium text-gray-800"
                                >{{ formatHufDisplay(cell.product ? cell.totalHuf : cell.baseHuf) }}</span>
                                <button
                                    v-if="cell.product"
                                    type="button"
                                    class="mt-0.5 shrink-0 rounded-md bg-red-600 px-3 py-1.5 text-xs font-bold text-white transition-colors hover:bg-red-700"
                                    @click="onBuy(cell.product)"
                                >
                                    {{ ctaBuy }}
                                </button>
                                <button
                                    v-else
                                    type="button"
                                    class="mt-0.5 shrink-0 rounded-md border border-red-200 bg-white px-3 py-1.5 text-xs font-bold text-red-700 transition-colors hover:bg-red-50"
                                    @click="goBuyFlow"
                                >
                                    {{ ctaBuy }}
                                </button>
                            </div>
                        </template>
                        <span v-else v-html="cell.html" />
                    </td>
                </tr>
            </tbody>
        </table>
    </div>
</template>

<script setup>
import { computed, watch, ref, onMounted } from 'vue';
import { useStore } from 'vuex';
import { getProductBasePrice, getProductServiceFee } from '../utils/productPricing';

defineOptions({ name: 'VignettePricingTable' });

const props = defineProps({
    /** Full HTML snippet from CMS (wrapper + table, or table only). Prices in HUF in cells from column 3 onward. */
    tableHtml: { type: String, default: '' },
});

const store = useStore();

const headerCells = ref([]);
const bodyRows = ref([]);

const texts = computed(() => store.getters.texts || {});
const products = computed(() => store.getters['form/products'] || []);

const currencies = computed(() => store.getters.currencies || []);
const storeCurrency = computed(() => store.getters['currency/currentCurrency'] || {});

const currencyDisplayKey = computed(() => {
    const c = storeCurrency.value || {};
    const code = String(c.code || '').trim().toUpperCase();
    const conv = conversionForCode(currencies.value, code);
    return `${code}|${conv}`;
});

const emptyMessage = computed(
    () => store.getters.texts?.['vignette-prices-pricing-table-empty'] ?? 'Pricing table is not available.'
);

const labelOfficial = computed(
    () => texts.value['vignette-prices.blade.php-th-official'] ?? 'Official'
);
const labelFee = computed(
    () => texts.value['vignette-prices.blade.php-th-fee'] ?? 'Service fee'
);
const labelTotal = computed(
    () => texts.value['vignette-prices.blade.php-th-total'] ?? 'Total'
);
const ctaBuy = computed(
    () => texts.value['vignette-prices.blade.php-cta-buy'] ?? 'Buy'
);

function conversionForCode(list, code) {
    const u = String(code ?? '').trim().toUpperCase();
    const c = (list || []).find((x) => String(x?.code ?? '').trim().toUpperCase() === u);
    const v = Number(c?.conversion ?? c?.rate ?? 0);
    return Number.isFinite(v) && v > 0 ? v : 0;
}

/**
 * CMS amounts are in HUF. Store `conversion` is "multiply EUR-base amount to show in this currency"
 * (same as PricingSection / VignettePrices). So: EUR = HUF / conv(HUF), then target = EUR * conv(target).
 * HUF selected: show CMS integers 1:1 (no EUR round-trip).
 */
function hufToDisplayAmount(huf, displayCode, list) {
    if (!Number.isFinite(huf) || huf < 0) return huf;
    const code = String(displayCode ?? '').trim().toUpperCase();
    const hufConv = conversionForCode(list, 'HUF');
    if (code === 'HUF') {
        return Math.round(huf);
    }
    if (!hufConv || hufConv <= 0) {
        return huf;
    }
    const eur = huf / hufConv;
    const targetConv = conversionForCode(list, code);
    if (!targetConv || targetConv <= 0) {
        return eur;
    }
    return eur * targetConv;
}

function parseHufPrice(rawText) {
    const text = String(rawText ?? '')
        .replace(/\u00a0/g, ' ')
        .replace(/&nbsp;/gi, ' ')
        .trim();
    if (!text) return null;
    const dec = text.replace(/\s/g, '').replace(/&mdash;/gi, '\u2014');
    if (/^[\u2014\u2013\u2212\-—]+$/i.test(dec)) return null;
    const m = text.match(/([\d\s.\u00A0]+)\s*(?:Ft|HUF)?/i);
    if (!m) return null;
    let n = m[1].replace(/\s/g, '').replace(/\u00A0/g, '');
    if (/^\d{1,3}(\.\d{3})+(\,\d+)?$/.test(n)) {
        n = n.replace(/\./g, '').replace(',', '.');
    } else {
        n = n.replace(',', '.');
    }
    const v = parseFloat(n);
    return Number.isFinite(v) ? v : null;
}

function stripHtml(html) {
    if (!html || typeof html !== 'string') return '';
    try {
        const d = new DOMParser().parseFromString(html, 'text/html');
        return (d.body?.textContent || '').replace(/\s+/g, ' ').trim();
    } catch {
        return html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
    }
}

/**
 * Map pricing column header (CMS) to duration in days.
 */
function headerTextToDays(headerHtml) {
    const t = stripHtml(headerHtml).toLowerCase();
    if (!t) return null;
    if (/\b10[\s-]*day/.test(t) || /\b10\b.*\bday/.test(t)) return 10;
    if (/\bmonthly\b|\b30[\s-]*day/.test(t) || /\b1\s*month\b/.test(t)) return 30;
    if (/\bannual\b|\b365\b|\byear(?:ly)?\b|\bnationwide\b/.test(t)) return 365;
    if (/\bdaily\b|\b1[\s-]*day\b/.test(t)) return 1;
    return null;
}

function rowCategoryFromFirstCell(cell) {
    if (!cell || cell.kind !== 'rich') return null;
    const t = stripHtml(cell.html);
    const m = t.match(/\b(D1M|D1|D2|U)\b/i);
    if (!m) return null;
    const g = m[1].toUpperCase();
    return g === 'D1M' ? 'D1M' : g;
}

function extractDays(title) {
    const match = String(title || '').match(/\d+/);
    return match ? Number(match[0]) : null;
}

function getDurationDays(product) {
    if (!product || typeof product !== 'object') return null;

    if (typeof product.duration_days === 'number') return product.duration_days;
    if (typeof product.duration === 'number' && product.duration >= 1) {
        return product.duration;
    }

    const titleSource =
        product.category?.translated?.title ||
        product.translated?.title ||
        '';
    const daysFromTitle = titleSource ? extractDays(String(titleSource)) : null;
    if (daysFromTitle) return daysFromTitle;

    const pathSource =
        product.category?.path ||
        product.path ||
        '';
    const match = String(pathSource).toLowerCase().match(/(\d+)[-_ ]*days?/);
    if (match) return Number(match[1]);

    return null;
}

function categoryCodeFromProduct(p) {
    const title = (p?.translated?.title || p?.category?.translated?.title || '').trim();
    const m = title.match(/\b(D1M|D1|D2|U)\b/i);
    if (m) {
        const g = m[1].toUpperCase();
        return g === 'D1M' ? 'D1M' : g;
    }
    const slug = String(p?.value || '').toLowerCase() + ' ' + String(p?.category?.value || '').toLowerCase();
    if (/\bd1m\b|\bd1-m\b|motorcycle/i.test(slug)) return 'D1M';
    if (/\bd1\b|\bd-?1\b|passenger/i.test(slug)) return 'D1';
    if (/\bd2\b|\bd-?2\b|van|motorhome/i.test(slug)) return 'D2';
    if (/\bu\b|trailer|caravan/i.test(slug)) return 'U';
    return null;
}

const productLookup = computed(() => {
    const m = new Map();
    for (const p of products.value) {
        if (!p || p.id == null) continue;
        const days = getDurationDays(p);
        const cat = categoryCodeFromProduct(p);
        if (!days || !cat) continue;
        m.set(`${cat}|${days}`, p);
    }
    return m;
});

function addFeeToBaseHuf(baseHuf, product, list) {
    if (!product || !Number.isFinite(baseHuf) || baseHuf < 0) {
        return { feeHuf: 0, totalHuf: baseHuf };
    }
    const apiBase = getProductBasePrice(product);
    const apiFee = getProductServiceFee(product);
    const apiTotal = apiBase + apiFee;
    if (apiBase > 0 && apiTotal >= apiBase) {
        const totalHuf = (baseHuf * apiTotal) / apiBase;
        const feeHuf = Math.max(0, totalHuf - baseHuf);
        return { feeHuf, totalHuf };
    }
    const hufConv = conversionForCode(list, 'HUF');
    const feeHuf = apiFee > 0 && hufConv > 0 ? apiFee * hufConv : 0;
    return { feeHuf, totalHuf: baseHuf + feeHuf };
}

function parseTable(html) {
    const empty = { headerCells: [], bodyRows: [] };
    if (!html || typeof html !== 'string') return empty;
    let doc;
    try {
        doc = new DOMParser().parseFromString(html, 'text/html');
    } catch {
        return empty;
    }
    const table = doc.querySelector('table');
    if (!table) return empty;

    const headRow = table.querySelector('thead tr');
    const hCells = headRow
        ? [...headRow.querySelectorAll('th, td')].map((cell) => cell.innerHTML.trim())
        : [];

    const bRows = [];
    table.querySelectorAll('tbody tr').forEach((tr) => {
        const cells = [...tr.querySelectorAll('td')];
        const row = cells.map((td, idx) => {
            if (idx < 2) {
                return { kind: 'rich', html: td.innerHTML };
            }
            const text = td.textContent || '';
            const huf = parseHufPrice(text);
            if (huf === null) {
                return { kind: 'rich', html: td.innerHTML };
            }
            return { kind: 'price', huf };
        });
        bRows.push(row);
    });

    return { headerCells: hCells, bodyRows: bRows };
}

watch(
    () => props.tableHtml,
    (h) => {
        const { headerCells: hC, bodyRows: bR } = parseTable(h || '');
        headerCells.value = hC;
        bodyRows.value = bR;
    },
    { immediate: true }
);

const columnDays = computed(() =>
    headerCells.value.map((h, i) => (i < 2 ? null : headerTextToDays(h)))
);

const displayBodyRows = computed(() => {
    const list = currencies.value;
    const lookup = productLookup.value;
    const daysArr = columnDays.value;

    return bodyRows.value.map((row) => {
        const cat = rowCategoryFromFirstCell(row[0]);
        return row.map((cell, ci) => {
            if (cell.kind !== 'price') {
                return { ...cell, html: cell.html };
            }
            const baseHuf = cell.huf;
            if (!Number.isFinite(baseHuf)) {
                return { kind: 'rich', html: cell.html || '' };
            }

            const days = daysArr[ci];
            const product =
                cat && days != null && lookup.get(`${cat}|${days}`)
                    ? lookup.get(`${cat}|${days}`)
                    : null;

            if (!product) {
                return {
                    kind: 'price',
                    baseHuf,
                    feeHuf: 0,
                    totalHuf: baseHuf,
                    product: null,
                };
            }

            const { feeHuf, totalHuf } = addFeeToBaseHuf(baseHuf, product, list);
            return {
                kind: 'price',
                baseHuf,
                feeHuf,
                totalHuf,
                product,
            };
        });
    });
});

function tdClass(cell, ci) {
    if (cell.kind === 'rich' && ci >= 2) {
        return 'px-4 py-4 text-right font-medium text-gray-700 tabular-nums sm:px-5 sm:py-5 whitespace-nowrap';
    }
    if (ci === 0) {
        return 'px-4 py-4 font-bold text-hu-green sm:px-5 sm:py-5';
    }
    if (ci === 1) {
        return 'px-4 py-4 text-gray-700 sm:px-5 sm:py-5';
    }
    return 'px-4 py-4 text-right align-top font-medium text-gray-700 tabular-nums sm:px-5 sm:py-5';
}

function formatHufDisplay(huf) {
    const list = currencies.value;
    const cur = storeCurrency.value || {};
    const code = String(cur.code || '').trim().toUpperCase();
    const sym = String(cur.symbol || '').trim() || '€';
    const amount = hufToDisplayAmount(huf, code, list);

    if (code === 'HUF') {
        return `${new Intl.NumberFormat('hu-HU', { maximumFractionDigits: 0 }).format(amount)} Ft`;
    }

    const formatted = new Intl.NumberFormat('de-DE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(amount);
    return `${sym} ${formatted}`.trim();
}

function goBuyFlow() {
    window.location.href = '/buy-vignette';
}

function onBuy(product) {
    if (!product?.id) return;
    const days = getDurationDays(product);
    const patch = {
        product_id: product.id,
        selected_duration: days ?? null,
    };
    const cid = product.category_id ?? product.category?.id;
    if (cid != null && cid !== '') {
        patch.category_id = cid;
    }
    store.commit('form/SET_FORM_DATA', patch);
    store.dispatch('form/saveDataToLocalStorage');
    window.location.href = '/buy-vignette';
}

onMounted(async () => {
    if (!products.value.length) {
        try {
            await store.dispatch('form/loadInitialData');
        } catch {
            /* ignore */
        }
    }
});
</script>
